import { faRightToBracket } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { register } from '@services/auth'
import { getCookie } from '@services/http'
import { type FormEvent, useState } from 'react'
import useSignIn from 'react-auth-kit/hooks/useSignIn'
import { useNavigate } from 'react-router-dom'
import { getFieldError, validateEmail, validatePassword } from '@/utils/validation'
import type { ValidationError } from '@/types/validationTypes'

const Registration = () => {
  const navigate = useNavigate()
  const signIn = useSignIn()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    []
  )

  const validateForm = () => {
    const errors: ValidationError[] = []
    const emailError = validateEmail(email)
    const passwordError = validatePassword(password)

    if (emailError) errors.push({ field: 'email', message: emailError })
    if (passwordError)
      errors.push({ field: 'password', message: passwordError })

    setValidationErrors(errors)
    return errors.length === 0
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (validationErrors.some((err) => err.field === 'email')) {
      const emailError = validateEmail(e.target.value)
      if (emailError) {
        setValidationErrors((prev) => {
          const filtered = prev.filter((e) => e.field !== 'email')
          return [...filtered, { field: 'email', message: emailError }]
        })
      } else {
        setValidationErrors((prev) => prev.filter((e) => e.field !== 'email'))
      }
    }
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    if (validationErrors.some((err) => err.field === 'password')) {
      const passwordError = validatePassword(e.target.value)
      if (passwordError) {
        setValidationErrors((prev) => {
          const filtered = prev.filter((e) => e.field !== 'password')
        return [...filtered, { field: 'password', message: passwordError }]
        })
      } else {
        setValidationErrors((prev) =>
          prev.filter((e) => e.field !== 'password')
        )
      }
    }
  }

  const handleEmailBlur = () => {
    const emailError = validateEmail(email)
    if (emailError) {
      setValidationErrors((prev) => {
        const filtered = prev.filter((e) => e.field !== 'email')
        return [...filtered, { field: 'email', message: emailError }]
      })
    } else {
      setValidationErrors((prev) => prev.filter((e) => e.field !== 'email'))
    }
  }

  const handlePasswordBlur = () => {
    const passwordError = validatePassword(password)
    if (passwordError) {
      setValidationErrors((prev) => {
        const filtered = prev.filter((e) => e.field !== 'password')
        return [...filtered, { field: 'password', message: passwordError }]
      })
    } else {
      setValidationErrors((prev) => prev.filter((e) => e.field !== 'password'))
    }
  }

  const handleRegister = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      const res = await register(email, password)
      const token = getCookie('jwt_token')

      if (!token) {
        throw new Error('No authentication token received')
      }

      signIn({
        auth: {
          token,
        },
        // TODO: configure refresh
        userState: { email, user_id: res.user_id, avatar: res.avatar },
      })

      navigate('/playground')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogin = () => {
    navigate('/playground')
  }

  return (
    <div className="relative w-full max-w-md max-h-max mx-auto my-auto p-6 border border-gray-200 rounded-lg shadow-lg bg-gradient-to-r from-blue-50 to-gray-50 transform transition-transform duration-300 hover:scale-[1.01]">
      <h1 className="text-secondary mb-6 text-4xl font-bold text-center">
        Registration
      </h1>

      {error && (
        <div className="text-red-500 mb-4 text-center p-3 bg-red-50 rounded-md border border-red-200">
          {error}
        </div>
      )}

      <form
        className="flex flex-col items-center w-full"
        onSubmit={handleRegister}
      >
        <div className="w-full max-w-sm">
          <input
            className="mb-4 p-3 text-2xl border border-gray-300 rounded-md w-full focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none transition-all duration-200"
            placeholder="Email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            disabled={isLoading}
          />
          {getFieldError(validationErrors, 'email') && (
            <span className="text-red-500 text-sm mb-2 block">
              {getFieldError(validationErrors, 'email')}
            </span>
          )}
        </div>
        <div className="w-full max-w-sm">
          <input
            className="mb-4 p-3 text-2xl border border-gray-300 rounded-md w-full focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none transition-all duration-200"
            type="password"
            placeholder="Password"
            value={password}
            onChange={handlePasswordChange}
            onBlur={handlePasswordBlur}
            disabled={isLoading}
          />
          {getFieldError(validationErrors, 'password') && (
            <span className="text-red-500 text-sm mb-2 block">
              {getFieldError(validationErrors, 'password')}
            </span>
          )}
        </div>
        <button
          type="submit"
          className="bg-primary p-4 my-4 text-secondary font-normal text-xl rounded-md cursor-pointer hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed w-full max-w-sm transition-colors duration-300 shadow-md"
          disabled={isLoading}
        >
          {isLoading ? 'Registering...' : 'Register'}
        </button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-gray-600 mb-2">Already have an account?</p>
        <button
          onClick={handleLogin}
          className="flex items-center justify-center mx-auto text-secondary hover:text-secondary-dark transition-colors duration-300"
        >
          <FontAwesomeIcon icon={faRightToBracket} className="mr-2 h-5 w-5" />
          <span>Login</span>
        </button>
      </div>
    </div>
  )
}

export default Registration
